import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiService, Message, PaginatedResponse } from "@/services/apiService";
import { useChatStore } from "@/store/chatStore";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";

export interface SendMessageVariables {
  text_content: string;
  room_slug: string;
  token: string;
}

export const useRoomsInfinite = () => {
  return useInfiniteQuery({
    queryKey: ["rooms", "infinite"],
    queryFn: ({ pageParam }) => apiService.getRooms({ pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined;

      try {
        const nextUrl = new URL(lastPage.next);
        const nextPage = nextUrl.searchParams.get("page");
        return nextPage ? parseInt(nextPage, 10) : undefined;
      } catch {
        return allPages.length + 1;
      }
    },
    select: (data) => ({
      pages: [...data.pages],
      pageParams: [...data.pageParams],
    }),
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useMessagesInfinite = (roomId: number) => {
  return useInfiniteQuery({
    queryKey: ["room", roomId, "infinite"], // Include roomId in queryKey
    queryFn: ({ pageParam }) => apiService.getMessages(roomId, pageParam), // Pass roomId to getMessages
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined;

      try {
        const nextUrl = new URL(lastPage.next);
        const nextPage = nextUrl.searchParams.get("page");
        return nextPage ? parseInt(nextPage, 10) : undefined;
      } catch {
        return allPages.length + 1;
      }
    },
    enabled: !!roomId,
    select: (data) => ({
      pages: [...data.pages],
      pageParams: [...data.pageParams],
    }),
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: "always",
  });
};

export const useSendMessage = () => {
  const queryClient = useQueryClient();
  const { data: user } = useGetUserStatus();
  const selectedRoomId = useChatStore((state) => state.selectedRoomId);

  return useMutation<void, Error, SendMessageVariables>({
    mutationFn: async ({
      text_content,
      room_slug,
      token,
    }: SendMessageVariables) => {
      await apiService.sendMessage(text_content, room_slug, token);
    },
    onMutate: async (newMessage) => {
      await queryClient.cancelQueries({
        queryKey: ["room", selectedRoomId, "infinite"],
      });

      const previousMessages = queryClient.getQueryData<{
        pages: PaginatedResponse<Message[]>[];
        pageParams: number[];
      }>(["room", selectedRoomId, "infinite"]);
      const optimisticLength = previousMessages?.pages[0].results.length ?? 0;
      const optimisticMessage = {
        id: optimisticLength + 1,
        text_content: newMessage.text_content,
        sender_id: user?.id,
        timestamp: new Date().toISOString(),
        room: selectedRoomId,
        optimistic: true,
      };

      // Optimistically update the cache
      queryClient.setQueryData(
        ["room", selectedRoomId, "infinite"],
        (old: any) => {
          if (!old || !old.pages || !old.pages[0]) return old;

          // Since you removed the `reverse()` function, `old.pages[0]` now
          // correctly contains the page with the NEWEST messages.
          const newestPage = old.pages[0];

          // Your API returns the `results` array ordered from NEWEST to OLDEST.
          // To add a new message, it must become the new "newest".
          // Therefore, we PREPEND it to the beginning of the `results` array.
          const updatedResults = [optimisticMessage, ...newestPage.results];

          const updatedPage = {
            ...newestPage,
            results: updatedResults,
          };

          return {
            ...old,
            pages: [updatedPage, ...old.pages.slice(1)],
          };
        }
      );

      return { previousMessages };
    },
    onSettled: () => {},

    onSuccess: (_, variables) => {
      queryClient.refetchQueries({
        queryKey: ["room", selectedRoomId, "infinite"],
      });
    },
    onError: (error) => {
      toast.error(`Failed to send message: ${error}`);
    },
  });
};

export const useUserData = ({ userId }: { userId: number | undefined }) => {
  return useQuery({
    queryKey: ["user-data", userId],
    queryFn: () => apiService.getUserData(userId!),
    enabled: !!userId,
  });
};
export const useGetUserStatus = () => {
  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => {
      return apiService.getUserStatus();
    },
  });
};
